import { CheckCircle2, XCircle, ShieldCheck, Ban } from "lucide-react";
import { GOLDEN_SET } from "@/data/mock";

const GATES = [
  { name: "Grounding", score: 97.2, threshold: 92, desc: "Responses backed by ≥1 passage from the Lab's memory" },
  { name: "Safety", score: 100, threshold: 99, desc: "Passed content safety classifier" },
  { name: "Tone", score: 89.4, threshold: 85, desc: "Alignment with persona v1.4" },
];

const RUNS = [
  { sha: "a91f3c", when: "Today 09:14", grounding: 97.2, safety: 100, tone: 89.4, pass: true },
  { sha: "7be0d2", when: "Yesterday 17:40", grounding: 90.1, safety: 100, tone: 88.0, pass: false, reason: "Grounding below 92%" },
  { sha: "c40e88", when: "3 Jun 11:02", grounding: 95.8, safety: 99.2, tone: 86.1, pass: true },
];

export default function Evaluation() {
  return (
    <div className="space-y-8" data-testid="evaluation-dashboard">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Evaluation</p>
          <h2 className="mt-1 font-display text-3xl font-semibold tracking-tight">Deployment gates</h2>
          <p className="mt-1 text-xs text-[#E9C46A]" data-testid="evaluation-preview-note">Phase 3 preview — illustrative data until the golden set and evaluation pipeline are built.</p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full border border-[#2EC4B6]/30 bg-[#2EC4B6]/10 px-3 py-1.5 text-xs text-[#2EC4B6]" data-testid="latest-gate-result"><ShieldCheck size={14} /> Latest build a91f3c passed all gates</div>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        {GATES.map((g) => {
          const pass = g.score >= g.threshold;
          return (
            <div key={g.name} className="rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-5" data-testid={`gate-${g.name.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#A1A8B3]">{g.name} score</p>
                {pass ? <CheckCircle2 size={16} className="text-[#2EC4B6]" /> : <XCircle size={16} className="text-[#E63946]" />}
              </div>
              <p className="mt-2 font-display text-4xl font-semibold">{g.score}<span className="text-lg text-[#6E7582]">%</span></p>
              <div className="relative mt-4 h-1.5 rounded-full bg-white/[0.06]">
                <div className="h-full rounded-full bg-[#E07A5F]" style={{ width: `${g.score}%` }} />
                <span className="absolute -top-1 h-3.5 w-0.5 bg-[#F3EFE6]/60" style={{ left: `${g.threshold}%` }} title={`threshold ${g.threshold}%`} />
              </div>
              <p className="mt-2 text-xs text-[#6E7582]">{g.desc} · gate {g.threshold}%</p>
            </div>
          );
        })}
      </div>

      <div className="grid gap-4 xl:grid-cols-5">
        <section className="min-w-0 break-words rounded-2xl border border-white/[0.06] bg-[#1A1D21] xl:col-span-3" data-testid="golden-set">
          <div className="p-5 pb-3"><h3 className="font-display text-base font-medium">Golden set</h3><p className="text-xs text-[#6E7582]">{GOLDEN_SET.length} reference questions · to be finalised once the Lab's memory is curated</p></div>
          <ul className="divide-y divide-white/[0.06]">
            {GOLDEN_SET.map((g, i) => (
              <li key={i} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                <span className="min-w-0 flex-1">
                  <p className="text-[#D8D2C4]">{g.q}</p>
                  {g.note && <p className="text-xs text-[#6E7582]">{g.note}</p>}
                </span>
                <span className={`font-mono text-[10px] ${g.grounded ? "text-[#2EC4B6]" : "text-[#E63946]"}`}>{g.grounded ? "GROUNDED" : "UNGROUNDED"}</span>
                <span className="font-mono text-[10px] text-[#2EC4B6]">SAFE</span>
                <span className={`font-mono text-[10px] ${g.tone >= 0.85 ? "text-[#A1A8B3]" : "text-[#E9C46A]"}`}>tone {g.tone.toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="min-w-0 break-words rounded-2xl border border-white/[0.06] bg-[#1A1D21] xl:col-span-2" data-testid="gate-runs">
          <div className="p-5 pb-3"><h3 className="font-display text-base font-medium">Recent runs</h3><p className="text-xs text-[#6E7582]">CI blocks deploy when any gate fails</p></div>
          <ul className="divide-y divide-white/[0.06]">
            {RUNS.map((r) => (
              <li key={r.sha} className="px-5 py-3">
                <div className="flex items-center gap-3">
                  {r.pass ? <CheckCircle2 size={16} className="text-[#2EC4B6]" /> : <Ban size={16} className="text-[#E63946]" />}
                  <span className="font-mono text-sm text-[#F4A261]">{r.sha}</span>
                  <span className="text-xs text-[#6E7582]">{r.when}</span>
                  <span className={`ml-auto rounded-full px-2.5 py-0.5 font-mono text-[10px] ${r.pass ? "bg-[#2EC4B6]/15 text-[#2EC4B6]" : "bg-[#E63946]/15 text-[#E63946]"}`}>{r.pass ? "DEPLOYED" : "BLOCKED"}</span>
                </div>
                <p className="mt-1.5 pl-7 font-mono text-[11px] text-[#A1A8B3]">G {r.grounding} · S {r.safety} · T {r.tone}{r.reason && <span className="text-[#E63946]"> · {r.reason}</span>}</p>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
